import { useState } from "react";
import { arrayMove } from "@dnd-kit/sortable";
import LiveCategoriesService from "../services/liveCategories.service";

export const useLiveCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Dialog crear/editar
  const [dialog, setDialog] = useState({
    open: false,
    mode: null, // 'create' | 'edit'
    category: null,
  });
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [formColor, setFormColor] = useState("#D0749B");
  const [formIcon, setFormIcon] = useState("Category");
  const [formBackground, setFormBackground] = useState("entrenamiento");
  const [formIsActive, setFormIsActive] = useState(true);

  const nameToSlug = (name) =>
    name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

  const handleSetFormName = (value) => {
    setFormName(value);
    if (!slugManuallyEdited) {
      setFormSlug(nameToSlug(value));
    }
  };

  const handleSetFormSlug = (value) => {
    setSlugManuallyEdited(true);
    setFormSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ""));
  };

  const getAll = async () => {
    try {
      setLoading(true);
      const response = await LiveCategoriesService.getAll();
      setCategories(response || []);
    } catch (error) {
      console.error("Error al obtener categorías:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Dialog ---
  const openCreateDialog = () => {
    setDialog({ open: true, mode: "create", category: null });
    setFormName("");
    setFormSlug("");
    setSlugManuallyEdited(false);
    setFormColor("#D0749B");
    setFormIcon("Category");
    setFormBackground("entrenamiento");
    setFormIsActive(true);
  };

  const openEditDialog = (category) => {
    setDialog({ open: true, mode: "edit", category });
    setFormName(category.name);
    setFormSlug(category.slug || "");
    setSlugManuallyEdited(true);
    setFormColor(category.color || "#D0749B");
    setFormIcon(category.icon || "Category");
    setFormBackground(category.background || "entrenamiento");
    setFormIsActive(category.isActive ?? true);
  };

  const closeDialog = () => {
    setDialog({ open: false, mode: null, category: null });
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      alert("El nombre es obligatorio");
      return;
    }
    if (!formSlug.trim()) {
      alert("El slug es obligatorio");
      return;
    }
    try {
      setLoading(true);
      if (dialog.mode === "create") {
        const nextOrder = categories.length + 1;
        await LiveCategoriesService.create({
          name: formName,
          slug: formSlug,
          order: nextOrder,
          isActive: formIsActive,
          color: formColor,
          icon: formIcon,
          background: formBackground,
        });
      } else {
        await LiveCategoriesService.update(dialog.category.id, {
          name: formName,
          slug: formSlug,
          isActive: formIsActive,
          color: formColor,
          icon: formIcon,
          background: formBackground,
        });
      }
      closeDialog();
      await getAll();
    } catch (error) {
      console.error("Error al guardar categoría:", error);
      alert("No se pudo guardar la categoría");
    } finally {
      setLoading(false);
    }
  };

  // --- Eliminar ---
  const handleDelete = async (category) => {
    const confirmed = window.confirm(
      `¿Eliminar la categoría "${category.name}"? Esta acción no se puede deshacer.`
    );
    if (!confirmed) return;
    try {
      setLoading(true);
      await LiveCategoriesService.remove(category.id);
      await getAll();
    } catch (error) {
      console.error("Error al eliminar categoría:", error);
      alert(error.message || "No se pudo eliminar la categoría");
    } finally {
      setLoading(false);
    }
  };

  // --- Drag and drop reorder ---
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    const reordered = arrayMove(categories, oldIndex, newIndex);

    const previous = categories;
    setCategories(reordered);

    try {
      await LiveCategoriesService.reorder(reordered.map((c) => c.id));
    } catch (err) {
      console.error("Error al guardar el orden:", err);
      setCategories(previous);
    }
  };

  return {
    categories,
    loading,
    dialog,
    formName,
    formSlug,
    formColor,
    formIcon,
    formBackground,
    formIsActive,
    setFormName: handleSetFormName,
    setFormSlug: handleSetFormSlug,
    setFormColor,
    setFormIcon,
    setFormBackground,
    setFormIsActive,
    getAll,
    openCreateDialog,
    openEditDialog,
    closeDialog,
    handleSave,
    handleDelete,
    handleDragEnd,
  };
};
